// ============================================================================
// HR & Hiring — landing page. 7-stage Kanban / List over hiring_requests with
// a shared filter bar, a Positions Count analytics slide-out, a structured
// New Position / Edit modal, and the Hiring → Onboarding hand-off.
//
// 2026-05-28 overhaul (Antonela spec §1 + §3.4): replaced the 3-column
// (Open/Covered/Not Covered) board with the 7-stage Notion workflow
// (Open · Interview · Onboarding · Filled · On Hold · Disregarded · No Status).
// Status normalization shipped in migration hr_hiring_pipeline_overhaul_2026_05_28.
//
// Tile drill-downs from the dashboard pass query params we read on mount:
//   ?scope=mine|all  ?stage=Open,Interview,On_Hold  ?filled=this_month|30d
//
// Scoping:
//   * superuser / hr → see + edit everything (RLS allows it).
//   * account_manager → see all (cover for each other), edit own; defaults to
//     "Mine". "Mine" = I requested it OR I'm the AM on its facility (Q5).
//   * other roles → no scope toggle; RLS returns their visible rows.
// ============================================================================

import { canEditAnyHiringRequest, canHardDelete, isAccountManagerSeat, seesHiringScopeSwitch } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BarChart3, Plus as PlusIcon, RefreshCw, X } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { Button, ErrorState, PageHeader, useToast } from '../../shared/components';
import {
  HIRING_STAGES,
  type HiringRequestStatus,
} from '../../shared/types';
import {
  useHiringRequests,
  useHiringFormOptions,
  addOpening,
  type HiringRequestRow,
} from './hooks/use-hiring-data';
import HiringKanban from './hiring-kanban';
import HiringList from './hiring-list';
import HiringFilters, {
  DEFAULT_HIRING_FILTERS,
  type HiringFilterState,
} from './hiring-filters';
import HiringAnalytics from './hiring-analytics';
import PositionForm from './position-form';
import HandoffModal from './handoff-modal';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';

type ViewMode = 'board' | 'list';

export default function HiringPipeline() {
  const { profile, employee } = useAuth();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const {
    data,
    isLoading,
    error,
    refetch,
    updateStatus,
    deleteRequest,
  } = useHiringRequests();
  const options = useHiringFormOptions();

  const role = profile?.role;
  const myEid = employee?.employee_id ?? profile?.employee_id ?? null;
  const showScope = seesHiringScopeSwitch(role);
  // national_am has full AM authority (has_full_am_authority): edit any record,
  // same as hr/superuser. Regular account_manager keeps own-records scope.
  const canEditAny = canEditAnyHiringRequest(role);
  // 2026-07-23 (P1 T5) — hard delete is server-gated by block_delete_func:
  // admin_force_delete only succeeds for superuser/executive (or a BYPASSRLS
  // role). Everyone else (e.g. Jack / national_am) got "Delete failed" on a
  // button they couldn't use. Gate the destructive path to those roles only;
  // non-admin editors get the "Mark as no longer needed" archive path instead.
  const mayHardDelete = canHardDelete(role);

  const [view, setView] = useState<ViewMode>('board');
  const [filters, setFilters] = useState<HiringFilterState>(() =>
    initFiltersFromParams(searchParams, showScope, role),
  );
  // Tile B / C pass a Filled date-window that isn't part of the chip filters.
  const [filledWindow] = useState<'this_month' | '30d' | null>(() => {
    const f = searchParams.get('filled');
    return f === 'this_month' || f === '30d' ? f : null;
  });

  const [showNew, setShowNew] = useState(false);
  const [editRow, setEditRow] = useState<HiringRequestRow | null>(null);
  // The People Desk links a request as ?open=HRQ-… (item 818): open it once
  // the rows are here, then leave the person to it.
  const [openedFromLink, setOpenedFromLink] = useState<string | null>(null);
  const openParam = searchParams.get('open');
  useEffect(() => {
    if (!openParam || openParam === openedFromLink || isLoading) return;
    setOpenedFromLink(openParam);
    const row = data.find((r) => r.request_id === openParam);
    if (row) setEditRow(row);
  }, [openParam, openedFromLink, isLoading, data]);
  const [handoffRow, setHandoffRow] = useState<HiringRequestRow | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [deleteRow, setDeleteRow] = useState<HiringRequestRow | null>(null);
  const [lastHandoff, setLastHandoff] = useState<{ id: string; name: string } | null>(
    null,
  );

  // Active accounts → hospital filter options + the New Position dropdown.
  const activeAccounts = useMemo(
    () =>
      options.accounts.filter(
        (a) => a.account_status === 'Active',
      ),
    [options.accounts],
  );

  const filtered = useMemo(() => {
    const now = Date.now();
    const d = new Date();
    const thisMonthStart = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    const cutoff30 = now - 30 * 86_400_000;

    return data.filter((r) => {
      const stage = (r.status ?? 'No_Status') as HiringRequestStatus;

      if (showScope && filters.scope === 'mine') {
        const mine =
          (!!profile?.id && r.requested_by === profile.id) ||
          (!!myEid &&
            (r.requested_by === myEid ||
              r.facility_account_manager_eid === myEid));
        if (!mine) return false;
      }
      if (
        filters.hospitals.length > 0 &&
        !(r.account_id && filters.hospitals.includes(r.account_id))
      )
        return false;
      if (
        filters.priorities.length > 0 &&
        !(r.urgency_label && filters.priorities.includes(r.urgency_label))
      )
        return false;
      if (filters.stages.length > 0 && !filters.stages.includes(stage))
        return false;

      if (filledWindow) {
        if (stage !== 'Filled' || !r.closed_date) return false;
        const t = Date.parse(r.closed_date);
        if (filledWindow === 'this_month' && t < thisMonthStart) return false;
        if (filledWindow === '30d' && t < cutoff30) return false;
      }
      return true;
    });
  }, [data, filters, filledWindow, showScope, profile?.id, myEid]);

  // Stage transition: a move to Onboarding opens the hand-off modal instead of
  // writing the status. Everything else is an optimistic status update.
  async function handleStageChange(row: HiringRequestRow, next: HiringRequestStatus) {
    if (next === 'Onboarding') {
      setEditRow(null);
      setHandoffRow(row);
      return;
    }
    await updateStatus(row.request_id, next);
  }

  async function handleDelete(row: HiringRequestRow, reason: string) {
    await deleteRequest(row.request_id, reason);
    setDeleteRow(null);
    setEditRow(null);
  }

  // Archive path for non-admin editors (P1 T5). "Disregarded" already filters
  // off the active board — a normal RLS-checked status write, no hard delete.
  async function handleArchive(row: HiringRequestRow) {
    try {
      await updateStatus(row.request_id, 'Disregarded');
      setEditRow(null);
      toast.success('Marked as no longer needed');
      refetch();
    } catch {
      toast.error('Could not update the position. Please try again.');
    }
  }

  return (
    <div className="mx-auto max-w-7xl p-4 pb-28 sm:p-6">
      <PageHeader
        title="Hiring pipeline"
        meta={
          isLoading
            ? 'Loading…'
            : `${filtered.length} of ${data.length} positions`
        }
        actions={
          <>
            {/* Ben, 2026-10-07 (item 818): an account manager must be able to start a
                hire from here without hunting for the +. Same form, same path; the
                floating + below stays for everyone who already uses it. */}
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowNew(true)}
              icon={<PlusIcon className="h-3.5 w-3.5" />}
            >
              Request a hire
            </Button>
            <div className="flex items-center gap-0.5 rounded-md border border-tcs-border bg-tcs-bg p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setView('board')}
                className={`rounded px-3 py-1 ${view === 'board' ? 'bg-tcs-surface font-medium text-tcs-text' : 'text-tcs-text-secondary'}`}
              >
                Board
              </button>
              <button
                type="button"
                onClick={() => setView('list')}
                className={`rounded px-3 py-1 ${view === 'list' ? 'bg-tcs-surface font-medium text-tcs-text' : 'text-tcs-text-secondary'}`}
              >
                List
              </button>
            </div>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowAnalytics(true)}
              icon={<BarChart3 className="h-3.5 w-3.5" />}
            >
              Analytics
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={refetch}
              disabled={isLoading}
              icon={<RefreshCw className="h-3.5 w-3.5" />}
            >
              Refresh
            </Button>
          </>
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorState body={error} onRetry={refetch} />
        </div>
      )}

      {/* Hand-off confirmation banner — Toast can't host a link, so we surface
          a dismissible deep-link to the Onboarding module here. */}
      {lastHandoff && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-st-ok-border bg-st-ok-bg px-4 py-2 text-sm text-st-ok-fg-strong">
          <span>
            Onboarding record <strong>{lastHandoff.id}</strong> created for{' '}
            {lastHandoff.name}.{' '}
            <Link to="/hr/onboarding" className="font-medium underline">
              Open in Onboarding module →
            </Link>
          </span>
          <button
            type="button"
            onClick={() => setLastHandoff(null)}
            aria-label="Dismiss"
            className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="mt-4">
        <HiringFilters
          filters={filters}
          onChange={setFilters}
          hospitalOptions={activeAccounts}
          showScope={showScope}
          visibleCount={filtered.length}
          totalCount={data.length}
        />
      </div>

      <div className="mt-4">
        {view === 'board' ? (
          <HiringKanban
            rows={filtered}
            onSelect={setEditRow}
            onStageChange={canEditAny ? handleStageChange : undefined}
          />
        ) : (
          <HiringList rows={filtered} onSelect={setEditRow} />
        )}
      </div>

      {!isLoading && data.length === 0 && !error && (
        <div className="mt-6 rounded-lg border border-dashed border-tcs-border-strong bg-tcs-raised p-8 text-center">
          <p className="text-sm text-tcs-text-secondary">
            No hiring requests yet. Tap + to create the first position.
          </p>
        </div>
      )}

      {/* New Position FAB */}
      <button
        type="button"
        onClick={() => setShowNew(true)}
        aria-label="New position"
        className="fixed bottom-6 right-6 flex h-16 w-16 items-center justify-center rounded-full bg-tcs-inverse text-tcs-inverse-ink shadow-lg transition-colors hover:bg-tcs-inverse-hover active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent focus-visible:ring-offset-2"
      >
        <PlusIcon className="h-7 w-7" strokeWidth={2.5} />
      </button>

      {showNew && (
        <PositionForm
          mode="create"
          accounts={options.accounts}
          facilities={options.facilities}
          myEid={myEid}
          onClose={() => setShowNew(false)}
          onCreated={(id, seats) => {
            // One request per person (item 621): the database opened `seats`.
            if (seats > 1) {
              toast.success(`Opened ${seats} requests — one per person.`, { description: `Starting at ${id}` });
            } else {
              toast.success('Position created', { description: id });
            }
            refetch();
          }}
        />
      )}

      {editRow && (
        <PositionForm
          mode="edit"
          row={editRow}
          accounts={options.accounts}
          facilities={options.facilities}
          myEid={myEid}
          canDelete={canEditAny}
          canHardDelete={mayHardDelete}
          onClose={() => setEditRow(null)}
          onSaved={() => {
            toast.success('Position updated');
            refetch();
          }}
          onAddOpening={async (row) => {
            await addOpening(row);
            const now = (row.seat_count ?? 1) + 1;
            toast.success(`Added another opening — ${now} for this job now.`);
          }}
          onMoveStage={(row, next) => handleStageChange(row, next)}
          onDelete={(row) => setDeleteRow(row)}
          onArchive={(row) => handleArchive(row)}
        />
      )}

      {handoffRow && (
        <HandoffModal
          row={handoffRow}
          onClose={() => setHandoffRow(null)}
          onCreated={(onboardingId, hireName) => {
            setHandoffRow(null);
            setLastHandoff({ id: onboardingId, name: hireName });
            toast.success(`Onboarding created for ${hireName}`, {
              description: onboardingId,
            });
            refetch();
          }}
        />
      )}

      {showAnalytics && (
        <HiringAnalytics rows={data} onClose={() => setShowAnalytics(false)} />
      )}

      {deleteRow && (
        <DeleteConfirm
          row={deleteRow}
          onClose={() => setDeleteRow(null)}
          onConfirm={(reason) => handleDelete(deleteRow, reason)}
        />
      )}

    </div>
  );
}

// Parse dashboard tile drill-down params into an initial filter state.
function initFiltersFromParams(
  params: URLSearchParams,
  showScope: boolean,
  role: string | undefined,
): HiringFilterState {
  const scopeParam = params.get('scope');
  const scope: 'mine' | 'all' =
    scopeParam === 'mine' || scopeParam === 'all'
      ? scopeParam
      : showScope && isAccountManagerSeat(role)
        ? 'mine'
        : 'all';

  const stageParam = params.get('stage');
  const stages = stageParam
    ? (stageParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => HIRING_STAGES.includes(s as HiringRequestStatus)) as HiringRequestStatus[])
    : [];

  return { ...DEFAULT_HIRING_FILTERS, scope, stages };
}

// Minimal reason-required confirm for the hard delete (admin_force_delete RPC).
function DeleteConfirm({
  row,
  onClose,
  onConfirm,
}: {
  row: HiringRequestRow;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const trimmed = reason.trim();

  async function go() {
    if (!trimmed) return;
    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm(trimmed);
    } catch (e) {
      setErr(errorMessage(e, 'Delete failed.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-tcs-scrim/60 p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border-2 border-st-danger-border-strong bg-tcs-overlay p-5 shadow-2xl"
      >
        <h3 className="text-base font-semibold text-st-danger-fg">Delete position</h3>
        <p className="mt-0.5 text-xs text-tcs-text-muted">{row.request_id}</p>
        <div className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg p-3 text-xs text-st-danger-fg-strong">
          <strong>Hard delete.</strong> The row is removed from{' '}
          <code>hiring_requests</code>; a snapshot is written to{' '}
          <code>deletion_requests</code> for audit. Cannot be undone here.
        </div>
        <label className="mt-4 block">
          <span className="text-xs font-medium text-tcs-text-secondary">
            Reason <span className="text-st-danger-fg">*</span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            autoFocus
            className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-field p-2 text-sm shadow-inner focus:border-tcs-accent focus:outline-none"
          />
        </label>
        {err && <p className="mt-2 text-xs text-st-danger-fg">{err}</p>}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs font-medium text-tcs-text-secondary hover:bg-tcs-bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={go}
            disabled={!trimmed || submitting}
            className="rounded-md bg-st-danger-solid px-3 py-1.5 text-xs font-medium text-st-solid-ink hover:bg-st-danger-solid disabled:opacity-50"
          >
            {submitting ? 'Working…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}
